import { ComponentChild, ComponentChildren } from 'preact';
import clsx from 'clsx';

import { InlineError } from 'components/InlineError';
import { Text } from 'components/Text';

import styles from './Labelled.module.scss';

interface LabelledProps {
  /** A unique identificator for the label */
  id: string;
  /** Text for the label */
  label: string;
  /** An icon to display before the label */
  icon?: ComponentChild;
  /** Error to display beneath the label */
  error?: string;
  /** Additional text to display */
  helpText?: string;
  /** Content to add label for */
  children: ComponentChildren;
  /** Hide the label visually */
  labelHidden?: boolean;
  /** Display red asterisk after the label */
  required?: boolean;
}

export function Labelled({
  id,
  label,
  icon,
  error,
  helpText,
  children,
  labelHidden,
  required,
}: LabelledProps) {
  const labelId = labelID(id);
  const helpTextId = helpTextID(id);
  const className = clsx(
    styles.Labelled,
    required && styles.required,
    labelHidden && styles.hidden,
  );

  const labelMarkup = (
    <div className={styles.LabelWrapper}>
      {icon && <div className={styles.Icon}>{icon}</div>}
      <label htmlFor={id} id={labelId} className={styles.Label}>
        {label}
      </label>
    </div>
  );

  const errorMarkup =
    error && typeof error !== 'boolean' ? (
      <div className={styles.Error}>
        <InlineError fieldId={id}>{error}</InlineError>
      </div>
    ) : null;

  const helpTextMarkup = helpText ? (
    <div className={styles.HelpText} id={helpTextId}>
      <Text as="span" size="small" variation="subdued">
        {helpText}
      </Text>
    </div>
  ) : null;

  return (
    <div className={className}>
      {labelMarkup}
      {children}
      {errorMarkup}
      {helpTextMarkup}
    </div>
  );
}

export function labelID(id: string) {
  return `${id}Label`;
}

export function helpTextID(id: string) {
  return `${id}HelpText`;
}
